import { useEffect, useRef, useCallback } from "react";

// Short iPhone-style tap sounds — percussive, snappy, < 100ms.
// Auto-attaches to every <button>. Skip a button with [data-no-sfx].
export default function useUiSounds() {
  const ctxRef = useRef(null);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) ctxRef.current = new Ctx();
    }
    return ctxRef.current;
  }, []);

  // A short percussive tap: tiny pitch-drop sine + brief filtered noise click
  const playTap = useCallback(
    (baseFreq, options = {}) => {
      const ctx = getCtx();
      if (!ctx) return;
      const { gain = 0.08, dropTo = 0.55, duration = 0.085 } = options;

      const t = ctx.currentTime;

      // master gentle low-pass so it doesn't sound bright/harsh
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 4500;
      lp.Q.value = 0.4;
      lp.connect(ctx.destination);

      // 1) main pitched body — quick exponential drop, fast decay
      const osc = ctx.createOscillator();
      const oG = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.exponentialRampToValueAtTime(
        baseFreq * dropTo,
        t + duration
      );
      oG.gain.setValueAtTime(0, t);
      oG.gain.linearRampToValueAtTime(gain, t + 0.004); // very fast attack
      oG.gain.exponentialRampToValueAtTime(0.0001, t + duration);
      osc.connect(oG);
      oG.connect(lp);
      osc.start(t);
      osc.stop(t + duration + 0.02);

      // 2) brief noise burst for the "tk" attack texture
      const bufferSize = Math.floor(ctx.sampleRate * 0.02);
      const noiseBuf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = noiseBuf.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        // decay across the buffer so it's a tiny click, not a hiss
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuf;
      const nG = ctx.createGain();
      nG.gain.setValueAtTime(gain * 0.3, t);
      nG.gain.exponentialRampToValueAtTime(0.0001, t + 0.025);

      // band-pass the noise to keep it clicky but not piercing
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 2200;
      bp.Q.value = 1.2;

      noise.connect(bp);
      bp.connect(nG);
      nG.connect(lp);
      noise.start(t);
      noise.stop(t + 0.03);
    },
    [getCtx]
  );

  // Click — variants get slightly different pitches
  const playClick = useCallback(
    (variant = "default") => {
      if (variant === "next") {
        // higher, snappier — feels like a forward confirm tap
        playTap(1100, { gain: 0.075, dropTo: 0.6, duration: 0.075 });
      } else if (variant === "back") {
        // lower, softer — feels like a back/dismiss tap
        playTap(700, { gain: 0.07, dropTo: 0.5, duration: 0.09 });
      } else {
        // neutral middle tap
        playTap(900, { gain: 0.07, dropTo: 0.55, duration: 0.08 });
      }
    },
    [playTap]
  );

  useEffect(() => {
    const handleClick = (e) => {
      const btn = e.target.closest("button");
      if (!btn || btn.disabled) return;
      if (btn.hasAttribute("data-no-sfx")) return;

      const text = (btn.textContent || "").toLowerCase().trim();
      let variant = "default";
      if (
        text.includes("next") ||
        text.includes("→") ||
        text.includes("continue") ||
        text.includes("one more") ||
        text.includes("yes")
      ) {
        variant = "next";
      } else if (
        text.includes("back") ||
        text.includes("←") ||
        text.includes("start over") ||
        text.includes("↺")
      ) {
        variant = "back";
      }
      playClick(variant);
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [playClick]);
}
